import React, { useState, useEffect, useCallback } from 'react';
import { amDesignColors, amDesignTypography, amDesignComponents } from '../themes/amDesignSystem';
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  CircularProgress, Alert, IconButton, Chip, Grid, Select, MenuItem, FormControl, InputLabel,
  Avatar, Stack, Divider, Tooltip, CardHeader, Snackbar, LinearProgress
} from '@mui/material';
import {
  Delete as DeleteIcon, Edit as EditIcon, Add as AddIcon, Close as CloseIcon,
  Category as CategoryIcon, Build as BuildIcon, CheckCircle as CheckCircleIcon,
  Warning as WarningIcon, AutoAwesome as AIIcon
} from '@mui/icons-material';
import { useBusiness } from '../contexts/BusinessContext';
import ServiceGeneratorWizard from './ServiceGeneratorWizard';

const ServiceManager = () => {
  const { selectedBusiness } = useBusiness();
  const [services, setServices] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openGeneratorWizard, setOpenGeneratorWizard] = useState(false);
  const [editingServiceIndex, setEditingServiceIndex] = useState(null);
  const [editingServiceType, setEditingServiceType] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [editingServiceName, setEditingServiceName] = useState('');  // Store service name for AI suggestions
  const [aiSuggestion, setAiSuggestion] = useState('');  // AI-generated suggestion
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);
  const [serviceData, setServiceData] = useState({
    type: 'structured',
    serviceTypeId: '',
    category: '',
    displayName: '',
    description: ''
  });
  const [editDescription, setEditDescription] = useState('');
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteServiceData, setDeleteServiceData] = useState({ index: null, type: 'freeForm', name: '' });

  const { freeFormServiceItems = [], structuredServiceItems = [], categories = [], serviceTypes = [] } = services;

  // Extract unique categories from existing free-form services
  const availableFreeFormCategories = [...new Set(categories.map(item => item.name))].sort();

  // Extract used structured service type IDs
  const usedStructuredServiceTypeIds = new Set(structuredServiceItems.map(item => item.serviceTypeId));

  // Get all available service types from backend (from categories)
  const allAvailableServiceTypes = serviceTypes.filter(st => !usedStructuredServiceTypeIds.has(st.serviceTypeId));

  // Format category display name (convert 'categories/gcid:gutter_service' to 'Gutter Service')
  const formatCategoryName = (categoryPath) => {
    // Extract the gcid part if it's a full path
    let category = categoryPath;
    if (categoryPath.includes(':')) {
      category = categoryPath.split(':')[1];
    }
    // Convert gutter_service to Gutter Service
    return category
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Build combined categories dropdown with used freeForm categories and unused structured types
  const categoriesForDropdown = [
    // Used free-form categories (can add multiple services)
    ...availableFreeFormCategories.map(cat => ({
      value: cat,
      label: formatCategoryName(cat),
      type: 'freeForm',
      isUsed: true
    })),
    // Unused structured service types (one service per type)
    ...allAvailableServiceTypes.map(service => ({
      value: service.serviceTypeId,
      label: service.displayName || formatCategoryName(service.serviceTypeId),
      type: 'structured',
      isUsed: false
    }))
  ];

  // Count services by category
  const servicesByCategory = {};

  freeFormServiceItems.forEach(item => {
    const categoryName = getServiceDisplayName(item.category);
    servicesByCategory[categoryName] = (servicesByCategory[categoryName] || 0) + 1;
  });

  // Fetch services for selected business
  const fetchServices = useCallback(async () => {
    if (!selectedBusiness?.id && !selectedBusiness?.googleLocationId) {
      setError('No business selected');
      return;
    }



   

    setLoading(true);
    setError(null);

    try {
      const locationId = selectedBusiness.googleLocationId || selectedBusiness.id;
      
      if (!locationId) {
        setError('Invalid business location ID');
        setLoading(false);
        return;
      }

      const response = await fetch(`/api/services/${locationId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch services: ${response.status}`);
      }

      const resp = await response.json();

      console.log('------resp',resp)
      setServices(resp.data || {});
    } catch (err) {
      console.error('Error fetching services:', err);
      setError(err.message || 'Failed to fetch services');
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness]);

  // Load services on component mount or when business changes
  useEffect(() => {
    if (selectedBusiness?.id || selectedBusiness?.googleLocationId) {
      setSelectedCategory(null); // Reset category filter when business changes
      fetchServices();
    }
  }, [selectedBusiness, fetchServices]);

  // Create new service
  const createService = async () => {
    try {
      setLoading(true);
      setError(null);

      const locationId = selectedBusiness.googleLocationId || selectedBusiness.id;

      if (!locationId) {
        throw new Error('Invalid business location ID');
      }

      const description = (serviceData.description || '').trim();
      let servicePayload;
      let requestPayload;

      if (serviceData.type === 'freeForm') {
        // Validate free-form service
        if (!serviceData.category || !serviceData.displayName) {
          throw new Error('Please fill in all required fields for free-form service');
        }

        servicePayload = {
          category: serviceData.category,
          label: {
            displayName: serviceData.displayName,
            ...(description && { description })
          }
        };

        requestPayload = {
          type: 'freeForm',
          serviceData: servicePayload
        };
      } else {
        // Validate structured service
        if (!serviceData.serviceTypeId) {
          throw new Error('Please select a service type');
        }

        servicePayload = {
          serviceTypeId: serviceData.serviceTypeId,
          ...(description && { description })
        };

        requestPayload = {
          type: 'structured',
          serviceData: servicePayload
        };
      }

      // Call API to create service
      const response = await fetch(`/api/services/${locationId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestPayload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('API Error Response:', errorData);
        throw new Error(errorData.message || errorData.error || `Failed to create service: ${response.status}`);
      }

      setSuccessMessage('Service created successfully!');
      setOpenAddDialog(false);

      // Reset form
      setServiceData({
        type: 'freeForm',
        serviceTypeId: '',
        category: '',
        displayName: '',
        description: ''
      });

      // Refresh services list
      fetchServices();

    } catch (err) {
      console.error('Error creating service:', err);
      setError(err.message || 'Failed to create service');
    } finally {
      setLoading(false);
    }
  };

  // Show delete confirmation dialog
  const handleOpenDeleteDialog = (serviceIndex, serviceType = 'freeForm', serviceName = '') => {
    setDeleteServiceData({ index: serviceIndex, type: serviceType, name: serviceName });
    setOpenDeleteDialog(true);
  };

  // Confirm and delete service
  const handleConfirmDelete = async () => {
    if (deleteServiceData.index === null) {
      return;
    }
    setOpenDeleteDialog(false);
    await deleteService(deleteServiceData.index, deleteServiceData.type);
    setDeleteServiceData({ index: null, type: 'freeForm', name: '' });
  };

  // Delete a service
  const deleteService = async (serviceIndex, serviceType = 'freeForm') => {
    try {
      setLoading(true);
      setError(null);

      const locationId = selectedBusiness.googleLocationId || selectedBusiness.id;

      if (!locationId) {
        throw new Error('Invalid business location ID');
      }

      const response = await fetch(`/api/services/${locationId}/${serviceIndex}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ serviceType })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('API Error Response:', errorData);
        throw new Error(errorData.message || errorData.error || `Failed to delete service: ${response.status}`);
      }

      const result = await response.json();

      setSuccessMessage('Service deleted successfully!');

      // Refresh services list
      fetchServices();

    } catch (err) {
      console.error('Error deleting service:', err);
      setError(err.message || 'Failed to delete service');
    } finally {
      setLoading(false);
    }
  };

  // Edit service description
  const handleOpenEditDialog = (serviceIndex, currentDescription, serviceName = '', serviceType = 'freeForm') => {
    setEditingServiceIndex(serviceIndex);
    setEditingServiceType(serviceType);
    setEditDescription(currentDescription || '');
    setEditingServiceName(serviceName || '');
    setAiSuggestion('');
    setOpenEditDialog(true);

    // Generate AI suggestion if service name is provided
    if (serviceName) {
      generateAiDescriptionSuggestion(serviceName);
    }
  };

  // Generate AI description suggestion
  const generateAiDescriptionSuggestion = async (serviceName) => {
    try {
      setLoadingSuggestion(true);
      const locationId = selectedBusiness.googleLocationId || selectedBusiness.id;

      const response = await fetch(`/api/services/${locationId}/generate-description`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ serviceName })
      });

      if (response.ok) {
        const data = await response.json();
        setAiSuggestion(data.description || '');
        console.log('✅ AI suggestion generated:', data.description);
      } else {
        console.error('Failed to generate suggestion:', response.status);
      }
    } catch (err) {
      console.error('Error generating AI suggestion:', err);
    } finally {
      setLoadingSuggestion(false);
    }
  };

  // Save edited service description
  const saveEditedDescription = async () => {
    try {
      setLoading(true);
      setError(null);

      const locationId = selectedBusiness.googleLocationId || selectedBusiness.id;

      if (!locationId) {
        throw new Error('Invalid business location ID');
      }

      if (editingServiceIndex === null) {
        throw new Error('No service selected for editing');
      }

      const response = await fetch(`/api/services/${locationId}/${editingServiceIndex}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          description: editDescription.trim(),
          serviceType: editingServiceType
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('API Error Response:', errorData);
        throw new Error(errorData.message || errorData.error || `Failed to update service: ${response.status}`);
      }

      setSuccessMessage('Service description updated successfully!');
      setOpenEditDialog(false);
      setEditingServiceIndex(null);
      setEditDescription('');

      // Refresh services list
      fetchServices();

    } catch (err) {
      console.error('Error updating service:', err);
      setError(err.message || 'Failed to update service');
    } finally {
      setLoading(false);
    }
  };

  const hasServices = freeFormServiceItems?.length > 0 || structuredServiceItems?.length > 0;

  const formatServiceName = (inputString) => {
    const match = inputString.match(/(?:[:/])([a-zA-Z_]+)$/);
    let lastPart = match ? match[1] : inputString;
    return lastPart
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
  };

  if (!selectedBusiness?.id && !selectedBusiness?.googleLocationId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
        Bitte wählen Sie ein Unternehmen aus der Dropdown-Liste, um dessen Services zu verwalten
        </Alert>
      </Box>
    );
  }

   function getServiceDisplayName(categoryName) {
  const category = categories.find(item => item.name === categoryName);
  return category ? category.displayName : null; // or return '' or throw error
}

function getDefinedServiceDisplayName(categoryName) {
  const category = serviceTypes.find(item => item.serviceTypeId === categoryName);
  return category ? category.displayName : null; // or return '' or throw error
}


const formatDisplayName = (id) =>
  id ? id.replace(/^job_type_id:/, '')
         .replace(/_/g, ' ')
         .replace(/\b\w/g, char => char.toUpperCase()) : '';

// Filter services based on selected category
const getFilteredFreeFormServices = () => {
  if (!selectedCategory) return freeFormServiceItems;
  return freeFormServiceItems.filter(service => {
    const categoryName = getServiceDisplayName(service?.category);
    return categoryName === selectedCategory;
  });
};

// For structured services, we would need to map them to categories
// Since structured services are by serviceTypeId, we return all if no filter
const getFilteredStructuredServices = () => {
  if (!selectedCategory) return structuredServiceItems;
  // For now, return empty if filter is selected, as structured services are type-based
  // You can modify this if structured services should also be filterable by category
  return [];
};

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <div>
              <Typography variant="h1" gutterBottom>
                Dienstleistungsverwaltung
              </Typography>

              <Typography
              variant="body2"
              sx={{
                color: amDesignColors.text.secondary,
                fontFamily: amDesignTypography.fontFamily.body
              }}
            >
                {selectedBusiness.businessName || selectedBusiness.displayName}
              </Typography>
            </div>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                startIcon={<AIIcon />}
                onClick={() => setOpenGeneratorWizard(true)}
                disabled={loading}
                sx={{ bgcolor: amDesignColors.success, '&:hover': { bgcolor: '#45A049' } }}
              >
                Generate Services
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenAddDialog(true)}
                disabled={loading}
              >
                Neuen Service hinzufügen
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Stats Overview - Services by Category */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Total Services Card */}
        {freeFormServiceItems?.length > 0 && (
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Card
              sx={{
                backgroundColor: selectedCategory === null ? 'primary.main' : '#E3F2FD',
                color: selectedCategory === null ? amDesignColors.white : 'inherit',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 3
                },
                ...(selectedCategory === null && { boxShadow: 4 })
              }}
              onClick={() => setSelectedCategory(null)}
            >
              <CardContent sx={{ textAlign: 'center' }}>
                <CategoryIcon sx={{ fontSize: 40, mb: 1, color: selectedCategory === null ? amDesignColors.white : 'primary.main' }} />
                <Typography variant="h4" sx={{ color: selectedCategory === null ? amDesignColors.white : 'inherit', fontWeight: 'bold' }}>{freeFormServiceItems?.length || 0}</Typography>
                <Typography variant="body2" sx={{ mt: 1, opacity: selectedCategory === null ? 0.9 : 1 }}>
                  Gesamte individuelle Dienstleistungen
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Services by Category */}
        {Object.entries(servicesByCategory).length > 0 ? (
          Object.entries(servicesByCategory).map(([category, count]) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={category}>
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  backgroundColor: selectedCategory === category ? 'success.light' : 'inherit',
                  border: selectedCategory === category ? '2px solid' : '1px solid',
                  borderColor: selectedCategory === category ? 'success.main' : 'divider',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 3
                  },
                  ...(selectedCategory === category && { boxShadow: 4 })
                }}
                onClick={() => setSelectedCategory(category)}
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  <CategoryIcon
                    color={selectedCategory === category ? 'success' : 'success'}
                    sx={{ fontSize: 40, mb: 1 }}
                  />
                  <Typography variant="h4">{count}</Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    {category}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                  Noch keine Services hinzugefügt
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Loading Indicator */}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Error Display */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Active Filter Indicator */}
      {selectedCategory && (
        <Alert
          severity="info"
          onClose={() => setSelectedCategory(null)}
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setSelectedCategory(null)}
            >
              Clear Filter
            </Button>
          }
        >
          Showing services for category: <strong>{selectedCategory}</strong>
        </Alert>
      )}

      {/* Services Tables */}
      {freeFormServiceItems?.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title={
              <Stack direction="row" alignItems="center" spacing={1}>
                <CategoryIcon />
                <Typography variant="h6"> Individuelle Dienstleistungen</Typography>
                <Chip label={getFilteredFreeFormServices().length} size="small" />
                {selectedCategory && <Chip label={`Filtered: ${selectedCategory}`} size="small" color="success" />}
              </Stack>
            }
          />
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Kategorie</strong></TableCell>
                  <TableCell><strong>Anzeigename</strong></TableCell>
                  <TableCell><strong>Beschreibung</strong></TableCell>
                  <TableCell align="center"><strong>Aktionen</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredFreeFormServices().map((service, index) => (
                  <TableRow key={`free-${index}`} hover>
                    <TableCell>
                      <Chip
                        label={getServiceDisplayName(service?.category) ||  formatDisplayName(service?.serviceTypeId)}
                        size="small"
                        color="secondary"
                        variant="outlined"
                        sx={{ backgroundColor: 'transparent' }}
                      />
                    </TableCell>
                    <TableCell>{service?.label?.displayName}</TableCell>
                    <TableCell>{service?.label?.description || ' No description'}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenEditDialog(index, service?.label?.description, service?.label?.displayName || service?.displayName)}
                        disabled={loading}
                        title="Edit description"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      {
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleOpenDeleteDialog(index, 'freeForm', service?.label?.displayName || service?.displayName || 'Unknown Service')}
                        disabled={loading}
                        title="Delete service"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>

                      }
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Structured services table */}
      {structuredServiceItems?.length > 0 && !selectedCategory && (
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title={
              <Stack direction="row" alignItems="center" spacing={1}>
                <BuildIcon />
                <Typography variant="h6">Structured Services</Typography>
                <Chip label={getFilteredStructuredServices().length} size="small" color="secondary" />
              </Stack>
            }
          />
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Service Type</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredStructuredServices().map((service, index) => (
                  <TableRow key={`structured-${index}`} hover>
                    <TableCell>
                      <Chip
                        label={getDefinedServiceDisplayName(service?.serviceTypeId) ||  formatDisplayName(service?.serviceTypeId) }
                        size="small"
                        color="secondary"
                        variant="outlined"
                        sx={{ backgroundColor: 'transparent' }}
                      />
                    </TableCell>
                    <TableCell>
                      {service?.description ? (
                        <>
                          {service?.label?.displayName && <strong>{service.label.displayName}</strong>}
                          {service?.label?.displayName && service?.label?.description && ' No description'}
                          {service?.description}
                        </>
                      ) : (
                        service?.label?.displayName ? <strong>{service?.label?.displayName}</strong> : ' No description'
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenEditDialog(index, service?.description, service?.label?.displayName || '', 'structured')}
                        disabled={loading}
                        title="Edit description"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>

{
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleOpenDeleteDialog(index, 'structured', service?.label?.displayName || 'Unknown Service')}
                        disabled={loading}
                        title="Delete service"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>

                }
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}


  {  /*   {categories?.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title={
              <Stack direction="row" alignItems="center" spacing={1}>
                <BuildIcon />
                <Typography variant="h6">Categories</Typography>
                <Chip label={categories.length} size="small" color="secondary" />
              </Stack>
            }
          />
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Service Type</strong></TableCell>
                 
                </TableRow>
              </TableHead>
              <TableBody>
                {services.categories.map((cat, index) => (
                  <TableRow key={`structured-${index}`} hover>
                    <TableCell>
                      <Chip 
                        label={cat} 
                        size="small" 
                        color="secondary" 
                        variant="outlined"
                      />
                    </TableCell>
                   
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )} */ }

      {!hasServices && !loading && (
        <Card sx={{ textAlign: 'center', p: 4 }}>
          <WarningIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Keine Services gefunden
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Dieses Unternehmen hat noch keine Services konfiguriert
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenAddDialog(true)}
          >
            Fügen Sie Ihren ersten Service hinzu
          </Button>
        </Card>
      )}

      {/* Add Service Dialog */}
      <Dialog 
        open={openAddDialog} 
        onClose={() => !loading && setOpenAddDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AddIcon />
            <Typography variant="h6">Neuen Service hinzufügen</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            {/* Service Type Selector */}
            <FormControl fullWidth>
              <InputLabel>Service Type *</InputLabel>
              <Select
                value={serviceData.type}
                label="Service Type *"
                onChange={(e) => {
                  const newType = e.target.value;
                  setServiceData({
                    ...serviceData,
                    type: newType,
                    category: '',
                    serviceTypeId: '',
                    displayName: '',
                    description: ''
                  });
                }}
                disabled={loading}
              >
                <MenuItem value="freeForm">
                  <span style={{ color: amDesignColors.primary }}>●</span>
                  <span style={{ marginLeft: '8px' }}>Free Form Service (Multiple per category)</span>
                </MenuItem>
                <MenuItem value="structured">
                  <span style={{ color: amDesignColors.primary }}>●</span>
                  <span style={{ marginLeft: '8px' }}>Structured Service (One per type)</span>
                </MenuItem>
              </Select>
            </FormControl>

            {/* Category/Service Type Selector */}
            <FormControl fullWidth>
              <InputLabel>
                {serviceData.type === 'freeForm' ? 'Category *' : 'Service Type *'}
              </InputLabel>
              <Select
                value={serviceData.type === 'freeForm' ? serviceData.category : serviceData.serviceTypeId}
                label={serviceData.type === 'freeForm' ? 'Category *' : 'Service Type *'}
                onChange={(e) => {
                  if (serviceData.type === 'freeForm') {
                    setServiceData({...serviceData, category: e.target.value});
                  } else {
                    setServiceData({...serviceData, serviceTypeId: e.target.value});
                  }
                }}
                disabled={loading}
              >
                {categoriesForDropdown && categoriesForDropdown.length > 0 ? (
                  categoriesForDropdown
                    .filter(cat => cat.type === serviceData.type)
                    .map((cat) => (
                      <MenuItem
                        key={cat.value}
                        value={cat.value}
                        sx={{
                          backgroundColor: '#E3F2FD',
                          '&:hover': {
                            backgroundColor: '#BBDEFB'
                          }
                        }}
                      >
                        <span style={{
                          color: amDesignColors.primary,
                          fontWeight: 'bold',
                          marginRight: '8px',
                          fontSize: '12px'
                        }}>
                          ●
                        </span>
                        {cat.type === 'freeForm' ? getServiceDisplayName(cat.value) : cat.label}
                        <span style={{
                          marginLeft: '8px',
                          fontSize: '11px',
                          color: amDesignColors.primary,
                          opacity: 0.7
                        }}>
                          ({cat.type === 'freeForm' ? 'Free Form' : 'Structured'})
                        </span>
                      </MenuItem>
                    ))
                ) : (
                  <MenuItem disabled>
                    {serviceData.type === 'freeForm'
                      ? 'No free-form categories available'
                      : 'No unused service types available'}
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            {/* Display Name - required for freeForm, optional for structured */}
            <TextField
              fullWidth
              label={serviceData.type === 'freeForm' ? 'Service Name *' : 'Service Name (Optional)'}
              value={serviceData.displayName}
              onChange={(e) => setServiceData({...serviceData, displayName: e.target.value})}
              placeholder={serviceData.type === 'freeForm' ? 'e.g., Wall Painting Service' : 'e.g., Standard Painting'}
              disabled={loading}
              required={serviceData.type === 'freeForm'}
            />

            {/* Description - optional for both */}
            <TextField
              fullWidth
              label="Description (Optional)"
              multiline
              rows={3}
              value={serviceData.description}
              onChange={(e) => setServiceData({...serviceData, description: e.target.value})}
              placeholder="Describe this service..."
              disabled={loading}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenAddDialog(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={createService}
            disabled={
              loading ||
              (serviceData.type === 'freeForm' && (!serviceData.category || !serviceData.displayName)) ||
              (serviceData.type === 'structured' && !serviceData.serviceTypeId)
            }
            startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {loading ? 'Creating...' : 'Create Service'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Service Description Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={() => !loading && setOpenEditDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <EditIcon />
            <Typography variant="h6">Edit Service Description</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            {editingServiceName && (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  Service: {editingServiceName}
                </Typography>
              </Box>
            )}

            {aiSuggestion && (
              <Box
                sx={{
                  p: 2,
                  bgcolor: '#E8F5E9',
                  border: '1px solid amDesignColors.success',
                  borderRadius: 1
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#2E7D32' }}>
                  💡 AI Suggestion:
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: '#1B5E20' }}>
                  {aiSuggestion}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setEditDescription(aiSuggestion)}
                  sx={{ color: '#2E7D32', borderColor: amDesignColors.success }}
                >
                  Use This Description
                </Button>
              </Box>
            )}

            {loadingSuggestion && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: amDesignColors.paper, borderRadius: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Generating AI suggestion...</Typography>
              </Box>
            )}

            {editingServiceName && !aiSuggestion && !loadingSuggestion && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<AIIcon />}
                onClick={() => generateAiDescriptionSuggestion(editingServiceName)}
                sx={{ color: amDesignColors.success, borderColor: amDesignColors.success }}
              >
                Generate AI Suggestion
              </Button>
            )}

            <TextField
              fullWidth
              label="Description"
              multiline
              rows={4}
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              placeholder="Enter service description..."
              disabled={loading}
              helperText={editDescription.length > 0 ? `${editDescription.length} characters` : ''}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenEditDialog(false);
              setEditingServiceIndex(null);
              setEditingServiceType(null);
              setEditDescription('');
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveEditedDescription}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <EditIcon />}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Service Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#d32f2f' }}>
          <DeleteIcon />
          Service löschen?
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                ⚠️ Diese Aktion kann nicht rückgängig gemacht werden!
              </Typography>
              <Typography variant="caption">
                Der Service wird dauerhaft gelöscht.
              </Typography>
            </Alert>

            <Box sx={{ p: 2, backgroundColor: '#ffebee', borderRadius: 1, border: '1px solid #ef5350', mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#c62828', mb: 1 }}>
                Service zum Löschen:
              </Typography>
              <Typography variant="body1" sx={{ color: '#d32f2f', fontWeight: 700 }}>
                {deleteServiceData.name}
              </Typography>
            </Box>

            <Typography variant="body2" color="textSecondary">
              Möchten Sie diesen Service wirklich löschen?
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            disabled={loading}
            variant="outlined"
            sx={{ flex: 1 }}
          >
            Abbrechen
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <DeleteIcon />}
            sx={{ flex: 1 }}
          >
            {loading ? 'Wird gelöscht...' : 'Ja, löschen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
        message={successMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />

      {/* Service Generator Wizard */}
      <ServiceGeneratorWizard
        open={openGeneratorWizard}
        onClose={() => setOpenGeneratorWizard(false)}
        selectedBusiness={selectedBusiness}
        onServicesGenerated={fetchServices}
      />
    </Box>
  );
};

export default ServiceManager;